import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    createTicketCategory,
    createTicketSubcategory,
    deleteTicketCategory,
    deleteTicketSubcategory,
    getTicketCategories,
    getTicketSubcategories,
    updateTicketCategory,
    updateTicketSubcategory,
} from '../../../../../helpers/ticket-helper';

export const ProjectTicketCatalogTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [categories, setCategories] = React.useState([]);
    const [subcategories, setSubcategories] = React.useState([]);

    const [categoryForm, setCategoryForm] = React.useState({ name: '', description: '' });
    const [subcategoryForm, setSubcategoryForm] = React.useState({ category: '', name: '', description: '' });

    const [isSavingCategory, setIsSavingCategory] = React.useState(false);
    const [isSavingSubcategory, setIsSavingSubcategory] = React.useState(false);
    const [isDeletingCategoryId, setIsDeletingCategoryId] = React.useState('');
    const [isDeletingSubcategoryId, setIsDeletingSubcategoryId] = React.useState('');
    const [editingCategoryId, setEditingCategoryId] = React.useState('');
    const [editingSubcategoryId, setEditingSubcategoryId] = React.useState('');
    const [isUpdatingCategoryId, setIsUpdatingCategoryId] = React.useState('');
    const [isUpdatingSubcategoryId, setIsUpdatingSubcategoryId] = React.useState('');
    const [deleteConfirm, setDeleteConfirm] = React.useState({
        isOpen: false,
        type: '',
        id: '',
        name: '',
    });

    const [categoryEditForm, setCategoryEditForm] = React.useState({
        name: '',
        description: '',
    });

    const [subcategoryEditForm, setSubcategoryEditForm] = React.useState({
        category: '',
        name: '',
        description: '',
    });

    const loadCatalogs = React.useCallback(async () => {
        if (!token || !projectId) {
            setError('No se pudo obtener la información de categorías del proyecto.');
            setCategories([]);
            setSubcategories([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const [categoriesResp, subcategoriesResp] = await Promise.all([
            getTicketCategories(token, projectId, { includeDeleted: false }),
            getTicketSubcategories(token, projectId, { includeDeleted: false }),
        ]);

        if (!categoriesResp?.success) {
            setError(categoriesResp?.message || 'No se pudo obtener el catálogo de categorías.');
            setCategories([]);
            setSubcategories([]);
            setIsLoading(false);
            return;
        }

        if (!subcategoriesResp?.success) {
            setError(subcategoriesResp?.message || 'No se pudo obtener el catálogo de subcategorías.');
            setCategories(Array.isArray(categoriesResp.data) ? categoriesResp.data : []);
            setSubcategories([]);
            setIsLoading(false);
            return;
        }

        const nextCategories = Array.isArray(categoriesResp.data) ? categoriesResp.data : [];
        const nextSubcategories = Array.isArray(subcategoriesResp.data) ? subcategoriesResp.data : [];

        setCategories(nextCategories);
        setSubcategories(nextSubcategories);
        setIsLoading(false);
    }, [token, projectId]);

    React.useEffect(() => {
        loadCatalogs();
    }, [loadCatalogs]);

    React.useEffect(() => {
        if (!subcategoryForm.category && categories.length > 0) {
            setSubcategoryForm((prev) => ({
                ...prev,
                category: categories[0]._id,
            }));
        }
    }, [categories, subcategoryForm.category]);

    const handleCategoryInputChange = (event) => {
        const { name, value } = event.target;
        setCategoryForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubcategoryInputChange = (event) => {
        const { name, value } = event.target;
        setSubcategoryForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCreateCategory = async () => {
        if (!token || !projectId || !categoryForm.name.trim() || isSavingCategory) {
            return;
        }

        setIsSavingCategory(true);
        setError('');
        const savingCategoryToastId = toast.loading('Creando categoría...');

        const resp = await createTicketCategory(token, projectId, {
            name: categoryForm.name.trim(),
            description: categoryForm.description,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo crear la categoría.';
            setError(message);
            toast.update(savingCategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSavingCategory(false);
            return;
        }

        setCategoryForm({ name: '', description: '' });
        toast.update(savingCategoryToastId, {
            render: resp?.message || 'Categoría creada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSavingCategory(false);
        await loadCatalogs();
    };

    const handleCreateSubcategory = async () => {
        if (
            !token
            || !projectId
            || !subcategoryForm.category
            || !subcategoryForm.name.trim()
            || isSavingSubcategory
        ) {
            return;
        }

        setIsSavingSubcategory(true);
        setError('');
        const savingSubcategoryToastId = toast.loading('Creando subcategoría...');

        const resp = await createTicketSubcategory(token, projectId, {
            category: subcategoryForm.category,
            name: subcategoryForm.name.trim(),
            description: subcategoryForm.description,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo crear la subcategoría.';
            setError(message);
            toast.update(savingSubcategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSavingSubcategory(false);
            return;
        }

        setSubcategoryForm((prev) => ({
            ...prev,
            name: '',
            description: '',
        }));
        toast.update(savingSubcategoryToastId, {
            render: resp?.message || 'Subcategoría creada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSavingSubcategory(false);
        await loadCatalogs();
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!token || !categoryId || isDeletingCategoryId) {
            return;
        }

        if (!(deleteConfirm.isOpen && deleteConfirm.type === 'category' && deleteConfirm.id === categoryId)) {
            const selectedCategory = categories.find((category) => category?._id === categoryId);
            setDeleteConfirm({
                isOpen: true,
                type: 'category',
                id: categoryId,
                name: selectedCategory?.name || '',
            });
            return;
        }

        setIsDeletingCategoryId(categoryId);
        setError('');
        const deletingCategoryToastId = toast.loading('Eliminando categoría...');

        const resp = await deleteTicketCategory(token, projectId, categoryId);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo eliminar la categoría.';
            setError(message);
            toast.update(deletingCategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsDeletingCategoryId('');
            setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
            return;
        }

        toast.update(deletingCategoryToastId, {
            render: resp?.message || 'Categoría eliminada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsDeletingCategoryId('');
        setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
        await loadCatalogs();
    };

    const handleStartEditCategory = (category) => {
        setEditingCategoryId(category._id);
        setCategoryEditForm({
            name: category.name || '',
            description: category.description || '',
        });
    };

    const handleCancelEditCategory = () => {
        setEditingCategoryId('');
        setCategoryEditForm({ name: '', description: '' });
    };

    const handleSaveEditCategory = async (categoryId) => {
        if (!token || !categoryId || !categoryEditForm.name.trim() || isUpdatingCategoryId) {
            return;
        }

        setIsUpdatingCategoryId(categoryId);
        setError('');
        const updatingCategoryToastId = toast.loading('Guardando categoría...');

        const resp = await updateTicketCategory(token, projectId, categoryId, {
            name: categoryEditForm.name.trim(),
            description: categoryEditForm.description,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar la categoría.';
            setError(message);
            toast.update(updatingCategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsUpdatingCategoryId('');
            return;
        }

        toast.update(updatingCategoryToastId, {
            render: resp?.message || 'Categoría actualizada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsUpdatingCategoryId('');
        handleCancelEditCategory();
        await loadCatalogs();
    };

    const handleDeleteSubcategory = async (subcategoryId) => {
        if (!token || !subcategoryId || isDeletingSubcategoryId) {
            return;
        }

        if (!(deleteConfirm.isOpen && deleteConfirm.type === 'subcategory' && deleteConfirm.id === subcategoryId)) {
            const selectedSubcategory = subcategories.find((subcategory) => subcategory?._id === subcategoryId);
            setDeleteConfirm({
                isOpen: true,
                type: 'subcategory',
                id: subcategoryId,
                name: selectedSubcategory?.name || '',
            });
            return;
        }

        setIsDeletingSubcategoryId(subcategoryId);
        setError('');
        const deletingSubcategoryToastId = toast.loading('Eliminando subcategoría...');

        const resp = await deleteTicketSubcategory(token, projectId, subcategoryId);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo eliminar la subcategoría.';
            setError(message);
            toast.update(deletingSubcategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsDeletingSubcategoryId('');
            setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
            return;
        }

        toast.update(deletingSubcategoryToastId, {
            render: resp?.message || 'Subcategoría eliminada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsDeletingSubcategoryId('');
        setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
        await loadCatalogs();
    };

    const handleStartEditSubcategory = (subcategory) => {
        const categoryId = typeof subcategory.category === 'object'
            ? subcategory.category?._id
            : subcategory.category;

        setEditingSubcategoryId(subcategory._id);
        setSubcategoryEditForm({
            category: categoryId || '',
            name: subcategory.name || '',
            description: subcategory.description || '',
        });
    };

    const handleCancelEditSubcategory = () => {
        setEditingSubcategoryId('');
        setSubcategoryEditForm({ category: '', name: '', description: '' });
    };

    const handleSaveEditSubcategory = async (subcategoryId) => {
        if (
            !token
            || !subcategoryId
            || !subcategoryEditForm.category
            || !subcategoryEditForm.name.trim()
            || isUpdatingSubcategoryId
        ) {
            return;
        }

        setIsUpdatingSubcategoryId(subcategoryId);
        setError('');
        const updatingSubcategoryToastId = toast.loading('Guardando subcategoría...');

        const resp = await updateTicketSubcategory(token, projectId, subcategoryId, {
            category: subcategoryEditForm.category,
            name: subcategoryEditForm.name.trim(),
            description: subcategoryEditForm.description,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar la subcategoría.';
            setError(message);
            toast.update(updatingSubcategoryToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsUpdatingSubcategoryId('');
            return;
        }

        toast.update(updatingSubcategoryToastId, {
            render: resp?.message || 'Subcategoría actualizada correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsUpdatingSubcategoryId('');
        handleCancelEditSubcategory();
        await loadCatalogs();
    };

    const closeDeleteConfirm = () => {
        if (isDeletingCategoryId || isDeletingSubcategoryId) {
            return;
        }

        setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirm.isOpen || !deleteConfirm.id) {
            return;
        }

        if (deleteConfirm.type === 'category') {
            await handleDeleteCategory(deleteConfirm.id);
            return;
        }

        if (deleteConfirm.type === 'subcategory') {
            await handleDeleteSubcategory(deleteConfirm.id);
        }
    };

    return (
        <div className="profile-details mb-1">
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Categorías y subcategorías</h5>
                <span className="badge bg-soft-primary text-primary">
                    {categories.length} categorías · {subcategories.length} subcategorías
                </span>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando catálogos...</div>}

            {!isLoading && error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {!isLoading && (
                <>
                    <div className="row g-3 mb-4">
                        <div className="col-lg-6">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3 h-100">
                                <h6 className="fw-bold mb-3">Nueva categoría</h6>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="project-category-name">Nombre</label>
                                    <input
                                        id="project-category-name"
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        value={categoryForm.name}
                                        onChange={handleCategoryInputChange}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="project-category-description">Descripción</label>
                                    <textarea
                                        id="project-category-description"
                                        name="description"
                                        className="form-control"
                                        rows={2}
                                        value={categoryForm.description}
                                        onChange={handleCategoryInputChange}
                                    />
                                </div>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateCategory} disabled={isSavingCategory}>
                                    {isSavingCategory ? 'Guardando...' : 'Agregar categoría'}
                                </button>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="border border-dashed border-gray-5 rounded-2 p-3 h-100">
                                <h6 className="fw-bold mb-3">Nueva subcategoría</h6>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="project-subcategory-category">Categoría</label>
                                    <select
                                        id="project-subcategory-category"
                                        name="category"
                                        className="form-select form-select-sm"
                                        value={subcategoryForm.category}
                                        onChange={handleSubcategoryInputChange}
                                        disabled={categories.length === 0}
                                    >
                                        {categories.length === 0 && (
                                            <option value="">Sin categorías disponibles</option>
                                        )}
                                        {categories.map((category) => (
                                            <option key={category._id} value={category._id}>{category.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="project-subcategory-name">Nombre</label>
                                    <input
                                        id="project-subcategory-name"
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        value={subcategoryForm.name}
                                        onChange={handleSubcategoryInputChange}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="project-subcategory-description">Descripción</label>
                                    <textarea
                                        id="project-subcategory-description"
                                        name="description"
                                        className="form-control"
                                        rows={2}
                                        value={subcategoryForm.description}
                                        onChange={handleSubcategoryInputChange}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleCreateSubcategory}
                                    disabled={isSavingSubcategory || categories.length === 0}
                                >
                                    {isSavingSubcategory ? 'Guardando...' : 'Agregar subcategoría'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="row g-3">
                        <div className="col-lg-6">
                            <div className="table-responsive border border-gray-3 rounded-2">
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-uppercase fs-11 fw-semibold py-3">Categoría</th>
                                            <th className="text-uppercase fs-11 fw-semibold py-3 text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map((category) => (
                                            <tr key={category._id}>
                                                <td className="py-2">
                                                    {editingCategoryId === category._id ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm mb-2"
                                                                value={categoryEditForm.name}
                                                                onChange={(event) => setCategoryEditForm((prev) => ({
                                                                    ...prev,
                                                                    name: event.target.value,
                                                                }))}
                                                            />
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows={2}
                                                                value={categoryEditForm.description}
                                                                onChange={(event) => setCategoryEditForm((prev) => ({
                                                                    ...prev,
                                                                    description: event.target.value,
                                                                }))}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="fw-semibold">{category.name || '-'}</div>
                                                            <div className="text-muted fs-12">{category.description || 'Sin descripción'}</div>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="py-2 text-end">
                                                    {editingCategoryId === category._id ? (
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-brand"
                                                                onClick={handleCancelEditCategory}
                                                                disabled={isUpdatingCategoryId === category._id}
                                                            >
                                                                Cancelar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleSaveEditCategory(category._id)}
                                                                disabled={isUpdatingCategoryId === category._id}
                                                            >
                                                                {isUpdatingCategoryId === category._id ? 'Guardando...' : 'Guardar'}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-brand"
                                                                onClick={() => handleStartEditCategory(category)}
                                                            >
                                                                Editar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-danger"
                                                                onClick={() => handleDeleteCategory(category._id)}
                                                                disabled={isDeletingCategoryId === category._id}
                                                            >
                                                                {isDeletingCategoryId === category._id ? 'Eliminando...' : 'Eliminar'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {categories.length === 0 && (
                                            <tr>
                                                <td className="text-center text-muted py-4" colSpan={2}>No hay categorías para este proyecto.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="table-responsive border border-gray-3 rounded-2">
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-uppercase fs-11 fw-semibold py-3">Subcategoría</th>
                                            <th className="text-uppercase fs-11 fw-semibold py-3">Categoría</th>
                                            <th className="text-uppercase fs-11 fw-semibold py-3 text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subcategories.map((subcategory) => (
                                            <tr key={subcategory._id}>
                                                <td className="py-2">
                                                    {editingSubcategoryId === subcategory._id ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm mb-2"
                                                                value={subcategoryEditForm.name}
                                                                onChange={(event) => setSubcategoryEditForm((prev) => ({
                                                                    ...prev,
                                                                    name: event.target.value,
                                                                }))}
                                                            />
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows={2}
                                                                value={subcategoryEditForm.description}
                                                                onChange={(event) => setSubcategoryEditForm((prev) => ({
                                                                    ...prev,
                                                                    description: event.target.value,
                                                                }))}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="fw-semibold">{subcategory.name || '-'}</div>
                                                            <div className="text-muted fs-12">{subcategory.description || 'Sin descripción'}</div>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="py-2">
                                                    {editingSubcategoryId === subcategory._id ? (
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={subcategoryEditForm.category}
                                                            onChange={(event) => setSubcategoryEditForm((prev) => ({
                                                                ...prev,
                                                                category: event.target.value,
                                                            }))}
                                                        >
                                                            {categories.map((category) => (
                                                                <option key={category._id} value={category._id}>{category.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (subcategory.category?.name || '-')}
                                                </td>
                                                <td className="py-2 text-end">
                                                    {editingSubcategoryId === subcategory._id ? (
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-brand"
                                                                onClick={handleCancelEditSubcategory}
                                                                disabled={isUpdatingSubcategoryId === subcategory._id}
                                                            >
                                                                Cancelar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleSaveEditSubcategory(subcategory._id)}
                                                                disabled={isUpdatingSubcategoryId === subcategory._id}
                                                            >
                                                                {isUpdatingSubcategoryId === subcategory._id ? 'Guardando...' : 'Guardar'}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-brand"
                                                                onClick={() => handleStartEditSubcategory(subcategory)}
                                                            >
                                                                Editar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light-danger"
                                                                onClick={() => handleDeleteSubcategory(subcategory._id)}
                                                                disabled={isDeletingSubcategoryId === subcategory._id}
                                                            >
                                                                {isDeletingSubcategoryId === subcategory._id ? 'Eliminando...' : 'Eliminar'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {subcategories.length === 0 && (
                                            <tr>
                                                <td className="text-center text-muted py-4" colSpan={3}>No hay subcategorías para este proyecto.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {deleteConfirm.isOpen && (
                        <>
                            <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
                                <div className="modal-dialog modal-dialog-centered" role="document">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Confirmar eliminación</h5>
                                            <button type="button" className="btn-close" aria-label="Close" onClick={closeDeleteConfirm} />
                                        </div>
                                        <div className="modal-body">
                                            <p className="mb-0">
                                                {deleteConfirm.type === 'category'
                                                    ? `¿Deseas eliminar la categoría ${deleteConfirm.name ? `"${deleteConfirm.name}"` : 'seleccionada'}?`
                                                    : `¿Deseas eliminar la subcategoría ${deleteConfirm.name ? `"${deleteConfirm.name}"` : 'seleccionada'}?`}
                                            </p>
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn btn-light-brand"
                                                onClick={closeDeleteConfirm}
                                                disabled={Boolean(isDeletingCategoryId) || Boolean(isDeletingSubcategoryId)}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleConfirmDelete}
                                                disabled={Boolean(isDeletingCategoryId) || Boolean(isDeletingSubcategoryId)}
                                            >
                                                {isDeletingCategoryId || isDeletingSubcategoryId ? 'Eliminando...' : 'Eliminar'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-backdrop fade show" />
                        </>
                    )}
                </>
            )}
        </div>
    );
};